import React, { useState } from 'react';
import { Button, Divider, Popover, Stack, Typography } from '@mui/material';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import { Nullable } from 'global';
import {
  FilterCardContainer,
  ListOfPresets,
  PresetFilter,
  PresetId,
  PresetMode,
  PresetsForm,
} from 'components/shared/Filters';
import { RenderWithCondition } from 'components/shared/Conditional/RenderWithCondition';

import { useClosedLostSavedViews } from '../../hooks/useClosedLostSavedViews';
import { ClosedLostFiltersState } from '../../types';

interface Props {
  filters: ClosedLostFiltersState;
  onApply: (filters: ClosedLostFiltersState) => void;
}

interface ModeState {
  type: PresetMode;
  presetId: Nullable<PresetId>;
}

export const ClosedLostSavedViews = ({ filters, onApply }: Props) => {
  const [anchorEl, setAnchorEl] = useState<Nullable<HTMLElement>>(null);
  const [mode, setMode] = useState<ModeState>({ type: PresetMode.OFF, presetId: null });

  const { views, isViewsLoading, isMutatingView, handleCreateView, handleUpdateView, handleDeleteView } =
    useClosedLostSavedViews();

  const presetsMap = views.reduce<Record<PresetId, PresetFilter<ClosedLostFiltersState>>>((acc, tf) => {
    acc[tf.tableFilterId] = {
      name: tf.name,
      id: tf.tableFilterId,
      isSelected: false,
      value: tf.config as ClosedLostFiltersState,
    };
    return acc;
  }, {});

  const isPresetFormVisible = [PresetMode.CREATE, PresetMode.UPDATE].includes(mode.type);

  const handleClose = () => {
    setAnchorEl(null);
    setMode({ type: PresetMode.OFF, presetId: null });
  };

  const handleSaveView = async ({ name }: { name: string }) => {
    if (mode.type === PresetMode.UPDATE && mode.presetId) {
      await handleUpdateView(mode.presetId, name, filters);
    } else {
      await handleCreateView(name, filters);
    }
    setMode({ type: PresetMode.OFF, presetId: null });
  };

  return (
    <>
      <Button size="small" variant="outlined" startIcon={<BookmarksIcon />} onClick={e => setAnchorEl(e.currentTarget)}>
        Saved Views
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Stack sx={{ width: 340, maxWidth: '90vw', p: 1.5 }} gap={1}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="subtitle2">Saved Views</Typography>
            <Button
              size="small"
              onClick={() => setMode({ type: PresetMode.CREATE, presetId: null })}
              disabled={isPresetFormVisible}
            >
              Save current view
            </Button>
          </Stack>

          <Divider />

          <RenderWithCondition condition={isPresetFormVisible}>
            <FilterCardContainer label={mode.type === PresetMode.UPDATE ? 'Update View' : 'Save View'}>
              <PresetsForm<ClosedLostFiltersState>
                key={mode.type}
                preset={mode.presetId ? presetsMap[mode.presetId] : null}
                onSave={handleSaveView}
                onCancel={() => setMode({ type: PresetMode.OFF, presetId: null })}
              />
            </FilterCardContainer>
          </RenderWithCondition>

          <ListOfPresets<ClosedLostFiltersState>
            list={Object.values(presetsMap)}
            isLoading={isViewsLoading}
            actionsLoading={isMutatingView}
            actions={{
              onSelect: presetId => {
                onApply(presetsMap[presetId].value);
                handleClose();
              },
              onEdit: presetId => setMode({ type: PresetMode.UPDATE, presetId }),
              onDelete: async presetId => {
                await handleDeleteView(presetId);
              },
            }}
          />
        </Stack>
      </Popover>
    </>
  );
};
